import { useEffect, useMemo, useRef, useState } from "react";
import { api, brl, fmtDate } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { StatusPill } from "@/components/StatusPill";
import { ChevronLeft, ChevronRight, CalendarDays, Users } from "lucide-react";
import { toast } from "sonner";

const DAYS_WINDOW = 21; // largura padrão em dias
const CELL_W = 44;
const ROW_H = 56;

function ymd(d) {
  const y = d.getFullYear(), m = String(d.getMonth() + 1).padStart(2, "0"), day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}
function addDays(d, n) { const x = new Date(d); x.setDate(x.getDate() + n); return x; }
function diffDays(a, b) { return Math.round((new Date(b) - new Date(a)) / 86400000); }
function todayLocal() { const d = new Date(); d.setHours(0, 0, 0, 0); return d; }

const STATUS_COLOR = {
  "pre-reserva": "bg-[#8B6A1F] border-[#6d5217] text-white",
  "hospedado":   "bg-[#083A6B] border-[#22392E] text-white",
  "ativa":       "bg-[#083A6B] border-[#22392E] text-white",
  "finalizada":  "bg-stone-400 border-stone-500 text-white",
  "cancelada":   "bg-stone-300 border-stone-400 text-stone-600 line-through",
};

export default function Calendario() {
  const [pousadas, setPousadas] = useState([]);
  const [quartos, setQuartos] = useState([]);
  const [reservas, setReservas] = useState([]);
  const [pousadaFilter, setPousadaFilter] = useState("all");
  const [startDate, setStartDate] = useState(todayLocal());
  const [detalheOpen, setDetalheOpen] = useState(false);
  const [reservaDetalhe, setReservaDetalhe] = useState(null);
  const [drag, setDrag] = useState(null); // {reserva, dxDays, dyRows, quartoIndex}
  const dragOverRef = useRef(null);

  useEffect(() => { load(); }, []);
  async function load() {
    const [p, q, r] = await Promise.all([
      api.get("/pousadas"), api.get("/quartos"), api.get("/reservas"),
    ]);
    setPousadas(p.data);
    // mantém containers (is_chacara_inteira / is_pousada_inteira) no state pra saber
    // quais reservas são "pousada inteira"; filtramos das rows físicas em quartosFiltrados
    setQuartos(q.data);
    setReservas(r.data);
  }

  const dias = useMemo(
    () => Array.from({ length: DAYS_WINDOW }, (_, i) => addDays(startDate, i)),
    [startDate]
  );
  const quartosFiltrados = useMemo(() => {
    const fisicos = quartos.filter((q) => !q.is_chacara_inteira && !q.is_pousada_inteira);
    const list = pousadaFilter === "all" ? fisicos : fisicos.filter((q) => q.pousada_id === pousadaFilter);
    return list.sort((a, b) => (a.pousada_id + a.numero).localeCompare(b.pousada_id + b.numero));
  }, [quartos, pousadaFilter]);

  const pousadaById = useMemo(() => Object.fromEntries(pousadas.map((p) => [p.id, p])), [pousadas]);
  const quartoById = useMemo(() => Object.fromEntries(quartos.map((q) => [q.id, q])), [quartos]);
  const hoje = ymd(todayLocal());
  const rangeStart = ymd(dias[0]);
  const rangeEnd = ymd(dias[dias.length - 1]);

  // Reservas visíveis no range (apenas para rows físicas — containers PI/CI viram faixa)
  const reservasVisiveis = useMemo(() => reservas.filter((r) => {
    if (!quartosFiltrados.some((q) => q.id === r.quarto_id)) return false;
    return r.data_entrada < rangeEnd && r.data_saida > rangeStart;
  }), [reservas, quartosFiltrados, rangeStart, rangeEnd]);

  // Faixas de Pousada Inteira: reservas cujo quarto é um container is_pousada_inteira.
  // Uma faixa atravessa TODAS as linhas visíveis da pousada correspondente no período.
  const piBands = useMemo(() => {
    const bands = [];
    reservas.forEach((r) => {
      const q = quartoById[r.quarto_id];
      if (!q?.is_pousada_inteira) return;
      if (!(r.data_entrada < rangeEnd && r.data_saida > rangeStart)) return;
      const idxs = [];
      quartosFiltrados.forEach((qq, i) => { if (qq.pousada_id === r.pousada_id) idxs.push(i); });
      if (idxs.length === 0) return;
      const firstIdx = idxs[0];
      const count = idxs[idxs.length - 1] - firstIdx + 1;
      const start = r.data_entrada < rangeStart ? rangeStart : r.data_entrada;
      const end = r.data_saida > rangeEnd ? rangeEnd : r.data_saida;
      bands.push({
        reserva: r,
        left: 240 + diffDays(rangeStart, start) * CELL_W,
        top: firstIdx * ROW_H,
        width: Math.max(CELL_W, diffDays(start, end) * CELL_W),
        height: count * ROW_H,
      });
    });
    return bands;
  }, [reservas, quartoById, quartosFiltrados, rangeStart, rangeEnd]);

  function onDragStart(e, reserva) {
    setDrag({ reserva });
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/plain", reserva.id);
  }
  function onDragOverCell(e, quartoId, dia) {
    e.preventDefault();
    dragOverRef.current = { quartoId, dia };
    e.dataTransfer.dropEffect = "move";
  }
  async function onDropCell(e, quartoId, diaStr) {
    e.preventDefault();
    const r = drag?.reserva;
    setDrag(null);
    if (!r) return;
    const duracao = diffDays(r.data_entrada, r.data_saida);
    const novaEntrada = diaStr;
    const novaSaida = ymd(addDays(new Date(diaStr), duracao));
    if (r.quarto_id === quartoId && r.data_entrada === novaEntrada) return; // nada mudou
    try {
      await api.patch(`/reservas/${r.id}`, {
        quarto_id: quartoId,
        data_entrada: novaEntrada,
        data_saida: novaSaida,
      });
      toast.success("Reserva movida");
      await load();
    } catch (err) {
      const msg = err?.response?.data?.detail || "Não foi possível mover: quarto/período indisponível";
      toast.error(msg);
      // reserva permanece no local original: nenhum estado local foi mutado antes do PATCH
    }
  }

  function openDetalhe(reserva) { setReservaDetalhe(reserva); setDetalheOpen(true); }

  return (
    <div data-testid="calendario-page">
      <PageHeader
        eyebrow="Motor de Reservas"
        title="Calendário"
        description="Visualização operacional das reservas por quarto e período."
      />

      <div className="flex flex-wrap items-center gap-3 mb-4">
        <Select value={pousadaFilter} onValueChange={setPousadaFilter}>
          <SelectTrigger data-testid="calendario-filter-pousada" className="w-64"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todas as pousadas</SelectItem>
            {pousadas.map((p) => <SelectItem key={p.id} value={p.id}>{p.nome}</SelectItem>)}
          </SelectContent>
        </Select>

        <div className="flex items-center gap-1 ml-auto">
          <Button data-testid="btn-cal-prev" size="sm" variant="outline"
            onClick={() => setStartDate(addDays(startDate, -7))}>
            <ChevronLeft size={14} className="mr-1" /> 7 dias
          </Button>
          <Button data-testid="btn-cal-hoje" size="sm"
            className="bg-[#083A6B] hover:bg-[#22392E] text-white"
            onClick={() => setStartDate(todayLocal())}>
            <CalendarDays size={14} className="mr-1" /> Hoje
          </Button>
          <Button data-testid="btn-cal-next" size="sm" variant="outline"
            onClick={() => setStartDate(addDays(startDate, 7))}>
            7 dias <ChevronRight size={14} className="ml-1" />
          </Button>
        </div>
      </div>

      <div className="border border-stone-200 rounded-lg overflow-hidden bg-[#FFFFFF]">
        <div className="overflow-x-auto">
          <div className="relative" style={{ minWidth: 240 + DAYS_WINDOW * CELL_W }}>
            {/* Cabeçalho dos dias */}
            <div className="flex sticky top-0 bg-stone-50 border-b border-stone-200 z-20">
              <div className="w-60 shrink-0 sticky left-0 bg-stone-50 z-30 px-4 py-2 text-xs uppercase tracking-widest text-stone-500 border-r border-stone-200">
                Quarto
              </div>
              {dias.map((d) => {
                const isToday = ymd(d) === hoje;
                return (
                  <div key={ymd(d)}
                    className={`shrink-0 text-center text-[10px] py-2 border-r border-stone-200 ${isToday ? "bg-[#EAF0EA] text-[#083A6B] font-semibold" : "text-stone-500"}`}
                    style={{ width: CELL_W }}>
                    <div>{d.toLocaleDateString("pt-BR", { weekday: "short" }).slice(0, 3)}</div>
                    <div>{String(d.getDate()).padStart(2, "0")}/{String(d.getMonth() + 1).padStart(2, "0")}</div>
                  </div>
                );
              })}
            </div>

            {/* Linhas de quartos */}
            <div className="relative" data-testid="calendario-rows">
            {quartosFiltrados.map((q) => (
              <div key={q.id} className="flex border-b border-stone-100 relative" style={{ height: ROW_H }}>
                <div data-testid={`row-quarto-${q.id}`}
                  className="w-60 shrink-0 sticky left-0 bg-[#FFFFFF] z-10 px-4 py-2 border-r border-stone-200 flex flex-col justify-center">
                  <div className="text-sm font-medium text-stone-800">Nº {q.numero}</div>
                  <div className="text-[10px] text-stone-500 truncate">{pousadaById[q.pousada_id]?.nome || "—"}</div>
                </div>
                {dias.map((d) => (
                  <div key={ymd(d)}
                    data-testid={`cell-${q.id}-${ymd(d)}`}
                    onDragOver={(e) => onDragOverCell(e, q.id, ymd(d))}
                    onDrop={(e) => onDropCell(e, q.id, ymd(d))}
                    className={`shrink-0 border-r border-stone-100 ${ymd(d) === hoje ? "bg-[#EAF0EA]/40" : ""}`}
                    style={{ width: CELL_W }} />
                ))}
                {reservasVisiveis.filter((r) => r.quarto_id === q.id).map((r) => {
                  const start = r.data_entrada < rangeStart ? rangeStart : r.data_entrada;
                  const end = r.data_saida > rangeEnd ? rangeEnd : r.data_saida;
                  const left = 240 + diffDays(rangeStart, start) * CELL_W;
                  const width = Math.max(CELL_W, diffDays(start, end) * CELL_W);
                  const cls = STATUS_COLOR[r.status] || "bg-stone-500 text-white border-stone-600";
                  return (
                    <button key={r.id}
                      draggable={r.status !== "cancelada" && r.status !== "finalizada"}
                      onDragStart={(e) => onDragStart(e, r)}
                      onClick={() => openDetalhe(r)}
                      data-testid={`cal-reserva-${r.id}`}
                      title={`${r.hospede_nome} · ${fmtDate(r.data_entrada)} → ${fmtDate(r.data_saida)}`}
                      className={`absolute rounded-md border text-[11px] px-2 py-1 shadow-sm text-left overflow-hidden hover:brightness-110 cursor-grab ${cls}`}
                      style={{ left: left + 2, top: 4, width: width - 4, height: ROW_H - 8 }}>
                      <div className="truncate font-medium">{r.hospede_nome || "—"}</div>
                      <div className="flex items-center gap-2 text-[10px] opacity-90">
                        <span className="inline-flex items-center gap-1"><Users size={10} />{r.num_pessoas || 1}</span>
                        <span className="truncate">{fmtDate(r.data_entrada)} → {fmtDate(r.data_saida)}</span>
                      </div>
                    </button>
                  );
                })}
              </div>
            ))}

            {/* Faixas de Pousada Inteira (overlay sobre todas as rows da pousada no período) */}
            {piBands.map((b) => (
              <button key={`pi-${b.reserva.id}`}
                onClick={() => openDetalhe(b.reserva)}
                data-testid={`cal-band-pi-${b.reserva.id}`}
                title={`Pousada Inteira · ${b.reserva.hospede_nome || "—"} · ${fmtDate(b.reserva.data_entrada)} → ${fmtDate(b.reserva.data_saida)}`}
                className="absolute rounded-md border-2 border-[#6d5217] bg-[#8B6A1F]/90 text-white text-left px-3 py-2 shadow-md hover:brightness-110 z-20 overflow-hidden"
                style={{
                  left: b.left + 2,
                  top: b.top + 4,
                  width: b.width - 4,
                  height: b.height - 8,
                  backgroundImage: "repeating-linear-gradient(45deg, rgba(255,255,255,0.12) 0 8px, transparent 8px 16px)",
                }}>
                <div className="text-[11px] font-bold uppercase tracking-widest flex items-center gap-1">
                  <span>🏨 Pousada Inteira</span>
                </div>
                <div className="text-[11px] mt-1 truncate">{b.reserva.hospede_nome || "—"}</div>
                <div className="text-[10px] opacity-90 truncate">
                  {fmtDate(b.reserva.data_entrada)} → {fmtDate(b.reserva.data_saida)}
                </div>
              </button>
            ))}
            </div>

            {quartosFiltrados.length === 0 && (
              <div className="p-10 text-center text-stone-500 text-sm">Nenhum quarto para exibir.</div>
            )}
          </div>
        </div>
      </div>

      {/* Legenda */}
      <div className="mt-3 flex flex-wrap gap-3 text-[10px] uppercase tracking-widest text-stone-500">
        {Object.entries(STATUS_COLOR).map(([s, cls]) => (
          <div key={s} className="flex items-center gap-1.5">
            <span className={`inline-block w-3 h-3 rounded ${cls}`} />
            {s}
          </div>
        ))}
      </div>

      <Dialog open={detalheOpen} onOpenChange={setDetalheOpen}>
        <DialogContent data-testid="cal-detalhe" className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl flex items-center gap-3">
              Detalhes da reserva
              {reservaDetalhe && <StatusPill status={reservaDetalhe.status} testid="cal-detalhe-status" />}
            </DialogTitle>
          </DialogHeader>
          {reservaDetalhe && (
            <div className="text-sm space-y-3">
              <div><div className="text-stone-500 text-xs">Hóspede</div><div className="font-medium text-stone-800" data-testid="cal-detalhe-hospede">{reservaDetalhe.hospede_nome || "—"}</div></div>
              <div className="grid grid-cols-2 gap-3">
                <div><div className="text-stone-500 text-xs">Pousada</div><div>{reservaDetalhe.pousada_nome}</div></div>
                <div><div className="text-stone-500 text-xs">Quarto</div><div>Nº {reservaDetalhe.quarto_numero}</div></div>
                <div><div className="text-stone-500 text-xs">Entrada</div><div>{fmtDate(reservaDetalhe.data_entrada)}</div></div>
                <div><div className="text-stone-500 text-xs">Saída</div><div>{fmtDate(reservaDetalhe.data_saida)}</div></div>
                <div><div className="text-stone-500 text-xs">Pessoas</div><div className="flex items-center gap-1"><Users size={12} />{reservaDetalhe.num_pessoas || 1}</div></div>
                <div><div className="text-stone-500 text-xs">Total</div><div className="font-serif text-[#083A6B]">{brl(reservaDetalhe.valor_total)}</div></div>
              </div>
              <div className="pt-3 border-t border-stone-200 text-xs text-stone-500">
                Para editar completamente, use a página <a href="/admin/reservas" className="text-[#083A6B] underline">Reservas</a>. Aqui você pode mover a reserva arrastando para outro dia ou quarto.
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
